import { Button } from 'components';
import Modal from 'components/Modal/Modal';
import Typography from 'components/Typography/Typography';
import theme from 'themes';
import { ModalSize } from 'types';
import { useTranslation } from 'react-i18next';
import {
  ButtonBox,
  FormGroup,
  ModalContainer,
  ModalHeader,
  Content,
  LabelField,
  InputField,
  FormControlDiv,
  FormRow,
  SmallText,
  TagTable,
  TagTableHeader,
  TagTableHeaderCell,
  TagTableBody,
  TagTableRow,
  TagTableCell
} from './CreateMachineModal.elements';
import FileUpload from 'common/components/FileUpload';
import { MachineData } from '../MachinesInTheLineWidget/MachinesInTheLineWidget';

interface CreateMachineModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  onSubmitBtn?: () => Promise<void>;
  handleFileChange?: (file: File) => void;
  uploadImage?: () => Promise<void>;
  machineData?: MachineData;
  handleMachineDataChange?: (field: string, value: string) => void;
  handleTagChange?: (index: number, field: string, value: string) => void;
}

const CreateMachineModal = ({
  isOpen,
  onRequestClose,
  onSubmitBtn,
  handleFileChange,
  uploadImage,
  machineData,
  handleMachineDataChange,
  handleTagChange,
}: CreateMachineModalProps): JSX.Element => {
  const { t } = useTranslation(['mh']);

  const handleApply = () => {
    // uploadImage();
    onSubmitBtn?.();
    onRequestClose();
  };

  return (
    <Modal visible={isOpen} onClose={onRequestClose} size={ModalSize.MEDIUM}>
      <ModalHeader>
        <Typography as="h3" mb={2} size="1.125rem" weight="bold">
          {t('Upload Machine Image')}
        </Typography>
        <SmallText>{t('Upload machine image and configure machine data')}</SmallText>
        <div className='divider'/>
      </ModalHeader>
      <ModalContainer>
        <Content>
          <FormRow>
            <FormControlDiv>
              <LabelField className='label' htmlFor='machine-name'>
                <span>{t('Machine name') as string}</span>
              </LabelField>
              <InputField
                type="text"
                id="machine-name"
                className='input'
                value={machineData?.machineName || ''}
                onChange={(e) => handleMachineDataChange?.('machineName', e.target.value)}
              />
            </FormControlDiv>
            <FormControlDiv>
              <LabelField className='label' htmlFor='machine-link'>
                <span>{t('Link to') as string}</span>
              </LabelField>
              <InputField
                type="text"
                id="machine-link"
                className='input'
                value={machineData?.machineLink || ''}
                onChange={(e) => handleMachineDataChange?.('machineLink', e.target.value)}
              />
            </FormControlDiv>
          </FormRow>
          <FormRow>
            <FormGroup style={{ width: '30%' }}>
              <FileUpload
              className="image-selector"
              acceptedTypes={{
                'application/image': ['.jpeg', '.webp', '.png', '.jpg']
              }}
              acceptedFileText=".jpg, .png, File size (10 MB max)."
              maxFileSize={10485760}
              onFileChange={(file) => {
                if (file && handleFileChange) {
                handleFileChange(file);
                }
              }}
              />
            </FormGroup>
            <FormControlDiv style={{ width: '70%'}}>
              <LabelField className='label' htmlFor='machine-description'>
                <span>{t('Machine and Image Description') as string}</span>
              </LabelField>
              <InputField
                type="textarea"
                id="machine-description"
                className='input'
                value={machineData?.machineDescription || ''}
                onChange={(e) => handleMachineDataChange?.('machineDescription', e.target.value)}
                style={{ flex: 1 }}
              />
            </FormControlDiv>
          </FormRow>
          <TagTable>
            <TagTableHeader>
              <TagTableRow>
                <TagTableHeaderCell>{t('Widget Tag Name')}</TagTableHeaderCell>
                <TagTableHeaderCell>{t('Target Value')}</TagTableHeaderCell>
                <TagTableHeaderCell>{t('Actual Value')}</TagTableHeaderCell>
              </TagTableRow>
            </TagTableHeader>
            <TagTableBody>
              {machineData?.MachineTags?.map((tag, index) => (
                <TagTableRow key={index}>
                  <TagTableCell>
                    <InputField
                      type="text"
                      id={`machine-tag-id-${index}`}
                      value={tag.id}
                      onChange={(e) => handleTagChange?.(index, 'id', e.target.value)}

                    />
                  </TagTableCell>
                  <TagTableCell>
                    <InputField
                      type="text"
                      id={`machine-tag-target-${index}`}
                      value={tag.targetValue || ''}
                      onChange={(e) => handleTagChange?.(index, 'targetValue', e.target.value)}
                    />
                  </TagTableCell>
                  <TagTableCell>
                    <InputField
                      type="text"
                      id={`machine-tag-actual-${index}`}
                      value={tag.actualValue || ''}
                      onChange={(e) => handleTagChange?.(index, 'actualValue', e.target.value)}
                    />
                  </TagTableCell>
                </TagTableRow>
              ))}
            </TagTableBody>
          </TagTable>
        </Content>
        <ButtonBox>
          <Button
            width="6.688rem"
            height="2rem"
            borderRadius="3rem"
            borderColor={theme.colors.donutChart.target}
            onClick={onRequestClose}
            color={theme.colors.donutChart.target}
          >
            {t('cancel')}
          </Button>
          <Button
            width="6.688rem"
            height="2rem"
            borderRadius="3rem"
            bgColor={theme.colors.donutChart.target}
            color="white"
            onClick={handleApply}
          >
            {t('apply')}
          </Button>
        </ButtonBox>
      </ModalContainer>
    </Modal>
  );
};

export default CreateMachineModal;
